import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import { SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { ValueChangedEvent } from 'devextreme/ui/select_box';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	debounceTime,
	firstValueFrom,
	map,
	Observable,
	takeUntil,
	tap,
	timer,
} from 'rxjs';

import { DxListsStorage, IDxList } from '../../../core/dx-lists.storage';
import {
	checkStringEquality,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
	HeaderFilterDefaultMapper,
	NoopValue,
} from '../../../core/dx-types';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	assignOrphanProjectsAdmins,
	initOrphanProjectsList,
	refreshOrphanProjectsList,
} from '../../store/orphan-project.actions';
import { OrphanProjectSelector } from '../../store/orphan-project.selector';
import {
	IOrphanProjectActionData,
	OrphanProjectAction,
	OrphanProjectActionPopupComponent,
} from '../orphan-project-action-popup/orphan-project-action-popup.component';
import { OrphanProjectItemEntity } from './orphan-project-item.entity';
import { OrphanProjectListEntity } from './orphan-project-list.entity';
import { ProjectAdminEntity } from './project-admin.entity';

@Component({
	selector: 'app-orphan-project-list',
	templateUrl: './orphan-project-list.component.html',
	styleUrls: ['./orphan-project-list.component.scss'],
})
export class OrphanProjectListComponent
	extends DcBaseComponent
	implements OnInit
{
	private readonly isFilteredViewSubject = new BehaviorSubject<boolean>(false);
	public projectAdminMapping: Map<number, string> = new Map();
	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public panelActions: Array<PanelAction> = [];
	public toggleActions = false;
	public selectionCount = 0;
	public projectsSummary = '';

	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};
	public vo$: Observable<{
		isLoading: boolean;
		list: OrphanProjectListEntity;
		admins: Array<ProjectAdminEntity>;
		isFirstLoad: boolean;
	}>;
	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;
	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	protected readonly DcLoaders = DcLoaders;
	protected readonly DxOperations = DxOperations;
	protected readonly ActionIcons = ActionIcons;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcIcons = DcIcons;
	public constructor(
		private readonly store: Store,
		private readonly orphanProjectSelector: OrphanProjectSelector,
		private readonly dxStorage: DxListsStorage,
		private readonly modalService: AppModalService,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'orphan-project-list';
		this.store.dispatch(initOrphanProjectsList());

		this.vo$ = combineAll({
			isLoading: this.orphanProjectSelector.getLoading$(),
			list: this.orphanProjectSelector.getOrphanProjectList$(),
			admins: this.orphanProjectSelector.getAdmins$(),
			isFirstLoad: this.firstLoadSubject,
		}).pipe(
			map(({ isLoading, list, admins, isFirstLoad }) => ({
				isLoading,
				list,
				admins,
				isFirstLoad,
			}))
		);
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.orphanProjectSelector
			.getOrphanProjectList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				tap((l) => {
					this.projectsSummary = $localize`:i18n=@@orphan-project.mapping.info:Description with
						${l.elements.size}:TOTAL_PROJECTS:,
						${this.projectAdminMapping.size}:TOTAL_MAPPED:, and
						${l.elements.size - this.projectAdminMapping.size}:TOTAL_UNMAPPED:.`;
					this.tagsHeaderFilter = l
						.extractUniqTags(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
				})
			)
			.subscribe();

		this.isFilteredViewSubject
			.pipe(
				takeUntil(this.onDestroy$),
				tap((isFiltered) => this.bootstrapIcons(isFiltered))
			)
			.subscribe();
	}

	public refresh(): void {
		this.firstLoadSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshOrphanProjectsList());
		this.projectAdminMapping.clear();
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.firstLoadSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: OrphanProjectItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();

			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public async onAdminSelectionChange(
		key: number,
		event: ValueChangedEvent
	): Promise<void> {
		if (event.value) {
			this.projectAdminMapping.set(key, event.value);
		} else {
			this.projectAdminMapping.delete(key);
		}
		await this.updateSummary();
	}

	public onGridSelectionChange($event: SelectionChangedEvent): void {
		this.selectionCount = $event.selectedRowsData.length;
		this.toggleActions = $event.selectedRowsData.length > 0;
	}

	public async save(hasUnmapped: boolean): Promise<void> {
		if (hasUnmapped) {
			const dialogRef = this.modalService.openPopup<IOrphanProjectActionData>(
				OrphanProjectActionPopupComponent,
				{
					title: $localize`:i18n=@@orphan-project.action-popup.title:`,
					subTitle: '',
					action: OrphanProjectAction.ConfirmSave,
				},
				{
					disableClose: true,
				}
			);

			const diagData = await firstValueFrom(dialogRef.afterClosed());
			if (!diagData) {
				return;
			}
		}
		this.store.dispatch(
			assignOrphanProjectsAdmins({
				projectAdmin: this.projectAdminMapping,
			})
		);
		this.projectAdminMapping.clear();
		this.grid?.instance.clearSelection();
		this.selectionCount = 0;
	}

	private async bootstrapIcons(filteredView: boolean): Promise<void> {
		const actions = [
			ActionIcons.Automatic,
			ActionIcons.Filter,
			ActionIcons.FilterOff,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): Promise<void> => this.filterUnmapped(),
				iconName: actionMap.get(
					filteredView ? ActionIcons.FilterOff : ActionIcons.Filter
				)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@orphan-project.action.filter-unmapped:`,
				typed: '',
			},
			{
				order: 1,
				command: (): Promise<void> => this.mapInBulk(),
				iconName: actionMap.get(ActionIcons.Automatic)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@orphan-project.action.bulk-map:`,
				typed: 'plain-context-action',
			},
		];
	}

	private async filterUnmapped(): Promise<void> {
		if (!this.grid || !this.projectAdminMapping.size) {
			// no need to filter if there are no mapped projects
			return;
		}
		const filteredView = await firstValueFrom(this.isFilteredViewSubject);
		if (!filteredView) {
			this.isFilteredViewSubject.next(true);
			const unmappedIds: Array<number> = [];
			const dataSource = this.grid.instance.getDataSource()?.items() ?? [];
			for (const project of dataSource) {
				if (!this.projectAdminMapping.has(project.id)) {
					unmappedIds.push(project.id);
				}
			}
			if (unmappedIds.length > 0) {
				const filter = unmappedIds.reduce<any>((acc, key, index) => {
					acc.push(['id', '=', key]);
					if (index < unmappedIds.length - 1) {
						acc.push('or');
					}
					return acc;
				}, []);
				this.grid.instance.filter(filter);
			}
		} else {
			this.isFilteredViewSubject.next(false);
			this.grid.instance.clearFilter();
		}
	}

	private async mapInBulk(): Promise<void> {
		const admins = await firstValueFrom(
			this.orphanProjectSelector.getAdmins$()
		);
		const dialogRef = this.modalService.openPopup<IOrphanProjectActionData>(
			OrphanProjectActionPopupComponent,
			{
				title: $localize`:i18n=@@orphan-project.action-popup.title:`,
				subTitle: '',
				admins,
				action: OrphanProjectAction.BulkMap,
			},
			{
				disableClose: true,
			}
		);
		const diagData = await firstValueFrom(dialogRef.afterClosed());
		if (
			this.grid &&
			diagData &&
			diagData.shouldLaunch &&
			diagData.selectedAdmin
		) {
			const selectedProjects = this.grid.instance.getSelectedRowKeys();
			selectedProjects.forEach((id) =>
				this.projectAdminMapping.set(id, diagData.selectedAdmin)
			);
			await this.updateSummary();
		}
	}

	private async updateSummary(): Promise<void> {
		const list = await firstValueFrom(
			this.orphanProjectSelector.getOrphanProjectList$()
		);
		this.projectsSummary = $localize`:i18n=@@orphan-project.mapping.info:Description with
						${list.elements.size}:TOTAL_PROJECTS:,
						${this.projectAdminMapping.size}:TOTAL_MAPPED:, and
						${list.elements.size - this.projectAdminMapping.size}:TOTAL_UNMAPPED:.`;
	}
}
